import {
  ArrowElbowDownRightIcon,
  ArrowElbowRightDownIcon,
  CaretDownIcon,
  CrosshairSimpleIcon,
  PlayIcon,
  type Icon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { RunModeBlocker } from '@erp/workbooks';
import type { WorkbookRunMode } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useRunModeText,
  useRunModes,
  type RunModesEditor,
} from './workbookRunModes';

export type RunMenuEditor = RunModesEditor & {
  run: (mode: WorkbookRunMode, targetId: string | null) => Promise<unknown>;
};

/** The Run modes a Target offers, in pipeline order: a whole-Workbook Run lives in the toolbar. */
const TARGET_MODES = ['up-to', 'only', 'from'] as const;
type TargetRunMode = (typeof TARGET_MODES)[number];

/** Down through this sheet, just this sheet, and from this sheet on down. */
const MODE_ICONS: Record<TargetRunMode, Icon> = {
  'up-to': ArrowElbowDownRightIcon,
  only: CrosshairSimpleIcon,
  from: ArrowElbowRightDownIcon,
};

/**
 * The Run modes for one Target (a Worksheet tab or cell, or an Output): each is
 * offered only when it can start, with the reason shown when it cannot. An
 * Output offers only Run Through This Output: it computes nothing itself.
 */
export function WorkbookRunMenu({
  editor,
  targetId,
  output = false,
  disabled,
  act,
}: {
  editor: RunMenuEditor;
  targetId: string;
  /** The Target is an Output. */
  output?: boolean;
  disabled: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
}) {
  const gt = useGT();
  const modes = useRunModes(editor, targetId);
  const { labels, blockerText } = useRunModeText(editor.definition);
  const descriptions: Record<TargetRunMode, string> = {
    'up-to': output
      ? gt(
          'Reload the data this output uses and recalculate the sheets it reads.',
          { context: 'workbook run mode description' },
        )
      : gt(
          'Reload the data this sheet uses and recalculate it and the sheets it depends on.',
          { context: 'workbook run mode description' },
        ),
    from: gt(
      'Reuse the last run’s data and recalculate this sheet and the sheets after it.',
      { context: 'workbook run mode description' },
    ),
    only: gt(
      'Reuse the last run’s data and recalculate only this sheet. Sheets after it keep their earlier results.',
      { context: 'workbook run mode description' },
    ),
  };
  const shown: readonly TargetRunMode[] = output ? ['up-to'] : TARGET_MODES;
  function reason(blocker: RunModeBlocker): string {
    return output && blocker.code === 'no-target'
      ? gt('Add a worksheet to this output first.', {
          context: 'workbook run mode blocked',
        })
      : blockerText(blocker);
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={disabled}
          aria-label={gt('Run options', { context: 'workbook run menu' })}
        >
          <PlayIcon />
          <T context="workbook run menu">Run</T>
          <CaretDownIcon className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {shown.map((mode) => {
          const option = modes[mode];
          const ModeIcon = MODE_ICONS[mode];
          return (
            <DropdownMenuItem
              key={mode}
              disabled={!option.allowed}
              className="items-start gap-2"
              onClick={() => void act(() => editor.run(mode, targetId))}
            >
              <ModeIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">
                  {output
                    ? gt('Run through this output', {
                        context: 'workbook run mode',
                      })
                    : labels[mode]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {option.blocker ? reason(option.blocker) : descriptions[mode]}
                </span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
